import React, { type ReactNode, useCallback, useRef } from 'react';

import { styled } from '@compiled/react';

import { token } from '@atlaskit/tokens';

import { useExtensionsState } from '../../../controllers/use-extensions-state';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const ItemSelectedToEditWrapperStyles = styled.div<{
	isSelectedToEdit: boolean;
	isSelectedToRemove: boolean;
}>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div[data-smart-link-container]': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles
		...({ isSelectedToEdit }) =>
			isSelectedToEdit && {
				outline: `${token('border.width.focused')} solid ${token('color.border.focused')}`,
			},
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles
		...({ isSelectedToRemove }) =>
			isSelectedToRemove && {
				outline: `${token('border.width.focused')} solid ${token('color.border.danger')}`,
			},
	},

	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'& div[data-smart-link-container]::before': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-dynamic-styles
		...({ isSelectedToRemove }) =>
			isSelectedToRemove && {
				content: '',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				bottom: 0,
				zIndex: 1,
				opacity: 0.5,
				backgroundColor: `${token('color.background.danger')}`,
			},
	},
});

type ItemSelectedToEditWrapperProps = {
	children: ReactNode;
	extensionLocalId: string;
	itemId: string;
};

export const ItemSelectedToEditWrapper = ({
	extensionLocalId,
	itemId,
	children,
}: ItemSelectedToEditWrapperProps) => {
	const itemRef = useRef<HTMLDivElement>(null);
	const [
		{ selectedExtensionLocalId, selectedToEditCardId, selectedToRemoveCardId },
		{ setIsInAutoFocusMode, setSelectedToEditCardId },
	] = useExtensionsState();

	const onItemClick = useCallback(() => {
		if (extensionLocalId === selectedExtensionLocalId) {
			setIsInAutoFocusMode(true);
			setSelectedToEditCardId(itemId);
		}
	}, [
		itemId,
		extensionLocalId,
		selectedExtensionLocalId,
		setSelectedToEditCardId,
		setIsInAutoFocusMode,
	]);

	const isSelectedToEdit =
		selectedToEditCardId === itemId && extensionLocalId === selectedExtensionLocalId;
	const isSelectedToRemove =
		selectedToRemoveCardId === itemId && extensionLocalId === selectedExtensionLocalId;

	return (
		<ItemSelectedToEditWrapperStyles
			ref={itemRef}
			isSelectedToEdit={isSelectedToEdit}
			isSelectedToRemove={isSelectedToRemove}
			onClick={onItemClick}
		>
			{children}
		</ItemSelectedToEditWrapperStyles>
	);
};
